import React, { useState } from 'react';
import { ethers } from 'ethers';
import { useContracts } from '../../hooks/useContracts';
import { useNotify } from '../../context/NotificationContext';
import { parseError } from '../../utils/errorParser';
import { fmtUSDT, fmtNum, fmtAgo, positionLetter } from '../../utils/format';
import { PACKAGE_NAMES } from '../../utils/constants';
import AdminSection from './AdminSection';
import ButtonLoader from '../common/ButtonLoader';

const StatRow = ({ label, value, accent }) => (
  <div className="lookup-row">
    <span className="lbl">{label}</span>
    <span className={`val ${accent || ''}`}>{value}</span>
  </div>
);

const AdminUserLookup = ({ role }) => {
  const { coreStorage } = useContracts();
  const notify = useNotify();
  const [query, setQuery] = useState('');
  const [busy, setBusy] = useState(false);
  const [data, setData] = useState(null);

  const lookup = async () => {
    if (!coreStorage?.read) return notify.error('Wallet', 'Connect wallet first');
    const q = query.trim();
    if (!q) return notify.error('Invalid', 'Enter a user ID or address');

    setBusy(true);
    setData(null);
    try {
      // Resolve userId
      let userId;
      if (ethers.utils.isAddress(q)) {
        const id = await coreStorage.read.addressToId(q);
        userId = id.toNumber ? id.toNumber() : Number(id);
        if (!userId) {
          notify.error('Not found', 'No user registered for this address');
          setBusy(false);
          return;
        }
      } else if (/^\d+$/.test(q)) {
        userId = Number(q);
      } else {
        notify.error('Invalid', 'Enter a numeric user ID or 0x address');
        setBusy(false);
        return;
      }

      // Fetch all in parallel
      const pkgStatusCalls = [1, 2, 3, 4, 5, 6, 7].map((i) =>
        coreStorage.read.getPackageStatus(userId, i).catch(() => null)
      );

      const [
        core, breakdown, eligibilities,
        eliteClaim, proClaim, globalClaim,
        held, eliteDirects,
        ...pkgs
      ] = await Promise.all([
        coreStorage.read.getUserCore(userId),
        coreStorage.read.getUserIncomeBreakdown(userId),
        coreStorage.read.getPoolEligibilities(userId),
        coreStorage.read.getEliteRoyaltyClaimable(userId).catch(() => 0),
        coreStorage.read.getProRoyaltyClaimable(userId).catch(() => 0),
        coreStorage.read.getGlobalRoyaltyClaimable(userId).catch(() => 0),
        coreStorage.read.getActiveHeldIncomes(userId).catch(() => ({ ids: [], amounts: [], packageIds: [], createdAts: [] })),
        coreStorage.read.getEliteEligibleDirectsCount(userId).catch(() => 0),
        ...pkgStatusCalls
      ]);

      if (!core || !core.account || core.account === ethers.constants.AddressZero) {
        notify.error('Not found', `User #${userId} does not exist`);
        setBusy(false);
        return;
      }

      setData({
        userId,
        core,
        breakdown,
        eligibilities,
        eliteClaim, proClaim, globalClaim,
        held,
        eliteDirects,
        pkgs
      });
    } catch (e) {
      notify.error('Lookup failed', parseError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AdminSection
      title="User Lookup"
      subtitle="Inspect any user by their on-chain ID or wallet address."
      role={role}
    >
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="User ID (e.g. 100123) or 0x address"
          onKeyDown={(e) => e.key === 'Enter' && lookup()}
          style={{ flex: 1, minWidth: 240 }}
        />
        <button className="btn btn-primary" onClick={lookup} disabled={busy}>
          {busy ? <ButtonLoader label="Looking up" /> : 'Lookup'}
        </button>
      </div>

      {data && (
        <div className="lookup-result" style={{ marginTop: 16 }}>
          {/* Header summary */}
          <div className="lookup-head">
            <div>
              <div className="card-sub">User ID</div>
              <div className="card-value yellow">#{data.userId}</div>
            </div>
            <div style={{ flex: 1, minWidth: 240 }}>
              <div className="card-sub">Wallet</div>
              <div style={{ fontFamily: 'var(--font-display)', fontSize: 12, wordBreak: 'break-all', color: 'var(--text-primary)' }}>
                {data.core.account}
              </div>
            </div>
            <div>
              <div className="card-sub">Status</div>
              <div className={`badge ${data.core.isActive ? 'badge-green' : 'badge-muted'}`}>
                {data.core.isActive ? 'ACTIVE' : 'INACTIVE'}
              </div>
            </div>
          </div>

          <div className="lookup-grid">
            {/* Core info */}
            <div className="lookup-card">
              <div className="card-title">Profile</div>
              <StatRow label="Sponsor" value={`#${data.core.sponsorId.toString()}`} />
              <StatRow label="Position Under Sponsor" value={positionLetter(data.core.position)} />
              <StatRow label="Highest Tier" value={`Tier ${data.core.highestPackage} – ${PACKAGE_NAMES[data.core.highestPackage - 1] || '—'}`} />
              <StatRow label="Direct Referrals" value={fmtNum(data.core.directReferrals.toString())} />
              <StatRow label="Elite-Eligible Directs" value={fmtNum(data.eliteDirects)} />
              <StatRow label="Total Received (Net)" value={`${fmtUSDT(data.core.totalReceived)} USDT`} accent="green" />
            </div>

            {/* Income breakdown */}
            <div className="lookup-card">
              <div className="card-title">Income (Gross)</div>
              <StatRow label="Sponsor"        value={`${fmtUSDT(data.breakdown.sponsorIncome)} USDT`} accent="yellow" />
              <StatRow label="Level"          value={`${fmtUSDT(data.breakdown.levelIncome)} USDT`} accent="green" />
              <StatRow label="Booster"        value={`${fmtUSDT(data.breakdown.boosterIncome)} USDT`} accent="yellow" />
              <StatRow label="Elite Royalty"  value={`${fmtUSDT(data.breakdown.eliteRoyalty)} USDT`} accent="green" />
              <StatRow label="Pro Royalty"    value={`${fmtUSDT(data.breakdown.proRoyalty)} USDT`} accent="yellow" />
              <StatRow label="Global Royalty" value={`${fmtUSDT(data.breakdown.globalRoyalty)} USDT`} accent="green" />
              <StatRow label="TOTAL (Gross)"  value={`${fmtUSDT(data.breakdown.totalIncomeGross)} USDT`} accent="yellow" />
            </div>

            {/* Pool eligibility & claimables */}
            <div className="lookup-card">
              <div className="card-title">Royalty Status</div>
              <StatRow
                label="Elite"
                value={<>
                  <span className={`badge ${data.eligibilities.eliteEligible ? 'badge-green' : 'badge-muted'}`}>
                    {data.eligibilities.eliteEligible ? 'YES' : 'NO'}
                  </span>
                  <span style={{ marginLeft: 8 }}>{fmtUSDT(data.eliteClaim)} claimable</span>
                </>}
              />
              <StatRow
                label="Pro"
                value={<>
                  <span className={`badge ${data.eligibilities.proEligible ? 'badge-green' : 'badge-muted'}`}>
                    {data.eligibilities.proEligible ? 'YES' : 'NO'}
                  </span>
                  <span style={{ marginLeft: 8 }}>{fmtUSDT(data.proClaim)} claimable</span>
                </>}
              />
              <StatRow
                label="Global"
                value={<>
                  <span className={`badge ${data.eligibilities.globalEligible ? 'badge-green' : 'badge-muted'}`}>
                    {data.eligibilities.globalEligible ? 'YES' : 'NO'}
                  </span>
                  <span style={{ marginLeft: 8 }}>{fmtUSDT(data.globalClaim)} claimable</span>
                </>}
              />
            </div>

            {/* Packages */}
            <div className="lookup-card">
              <div className="card-title">Tier Status</div>
              {data.pkgs.map((p, i) => (
                <StatRow
                  key={i}
                  label={`Tier ${i + 1} – ${PACKAGE_NAMES[i]}`}
                  value={p && p.isActive ? (
                    <span className="badge badge-green">
                      {fmtUSDT(p.totalEarned)} / {fmtUSDT(p.ceilingLimit)}
                    </span>
                  ) : <span className="badge badge-muted">LOCKED</span>}
                />
              ))}
            </div>

            {/* Held income */}
            <div className="lookup-card" style={{ gridColumn: '1 / -1' }}>
              <div className="card-title">Active Held Income</div>
              {data.held.ids.length === 0 ? (
                <div className="empty" style={{ padding: '12px 0' }}>
                  <div className="empty-title">No active holds</div>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 8 }}>
                  {data.held.ids.map((id, i) => (
                    <div key={id.toString()} className="held-card">
                      <div>
                        <div className="amt">{fmtUSDT(data.held.amounts[i])} USDT</div>
                        <div className="meta">
                          ID #{id.toString()} · Tier {data.held.packageIds[i].toString()} · created {fmtAgo(Number(data.held.createdAts[i]))}
                        </div>
                      </div>
                      <span className="badge badge-yellow">HELD</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </AdminSection>
  );
};

export default AdminUserLookup;
